import Constants from '@/Constants';
import { EntityType } from '@/models/EntityTypes';
import List from '@/models/List';
import { Workshop } from '@/models/Workshop';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './style.scss';

interface Props {
    course: List;
    index: number;
    workshopFmt: (Workshop & { conflicts: string[] }) | null;
    programClassStartDate?: Date;
    onClick: () => void;
}

const ClassFormWorkshopItem: React.FC<Props> = ({ course, index, workshopFmt, programClassStartDate, onClick }: Props) => {
    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const isSaveInProgress: boolean = useSelector((state: ApplicationState) => state.programClass.loading);

    const [isClicked, setIsClicked] = useState<boolean>(false);

    const history = useHistory();

    const onConfigureWorkshopClass = (): void => {
        onClick();
        setIsClicked(true);
    };

    const navigateToWorkshopConfiguration = (): void => {
        if (isClicked && !isSaveInProgress) {
            history.push(`${history.location.pathname}/courses/${course.id}`);
        }
    };

    useEffect(navigateToWorkshopConfiguration, [isClicked && isSaveInProgress]);

    return (
        <div className="class-form-workshop-item">
            <span className="class-form-workshop-item-number">{index}</span>
            <div className={`class-form-workshop-item-card ${workshopFmt && workshopFmt.conflicts.length > 0 ? 'conflict' : ''}`}>
                <img alt={course.name} className="class-form-workshop-item-card__thumbnail" loading="lazy" src={course.mainImage} />
                <div className="class-form-workshop-item-card__info">
                    <span className="class-form-workshop-item-card__info-badge">
                        {course.type === EntityType.WORKSHOP ? strings.WORKSHOP : strings.COURSE}
                    </span>
                    <p className="class-form-workshop-item-card__info-title">{course.name}</p>
                    {workshopFmt && (
                        <div>
                            <span className="class-form-workshop-item-card__info-type">
                                {workshopFmt.isOnline ? strings.WORKSHOP_TYPE_ONLINE : strings.WORKSHOP_TYPE_IN_PERSON}
                            </span>
                            <span className="class-form-workshop-item-card__info-qty">
                                {workshopFmt.meetings.length > 1 ? strings.WORKSHOPS : strings.WORKSHOP}
                            </span>
                            {workshopFmt.meetings.map((meeting) => (
                                <div
                                    className={`class-form-workshop-item-card__info-class-period ${meeting.hasConflict ? 'conflict' : ''}`}
                                    key={meeting.id}
                                >
                                    <span>{courseService.getWorkshopDateWithEventPeriod(meeting.startDate, meeting.endDate, strings)}</span>
                                </div>
                            ))}
                            {workshopFmt.conflicts.length > 0 && (
                                <div className="class-form-workshop-item-card__info-conflicts">
                                    {workshopFmt.conflicts.map((conflict, index) => (
                                        <div className="class-form-workshop-item-card__info-conflicts__container" key={index}>
                                            <i className="material-icons align-middle">{Constants.icons.REPORT_PROBLEM}</i>
                                            <span
                                                className="class-form-workshop-item-card__info-conflicts__item"
                                                dangerouslySetInnerHTML={{ __html: conflict }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {course.type === EntityType.WORKSHOP &&
                    (workshopFmt ? (
                        <button
                            className="class-form-workshop-item-card__actions"
                            disabled={isSaveInProgress}
                            onClick={onConfigureWorkshopClass}
                            type="button"
                        >
                            {strings.EDIT}
                        </button>
                    ) : (
                        <button
                            className="class-form-workshop-item-card__actions outline"
                            disabled={!programClassStartDate || isSaveInProgress}
                            onClick={onConfigureWorkshopClass}
                            type="button"
                        >
                            {strings.SET_UP}
                        </button>
                    ))}
            </div>
        </div>
    );
};

export default memo(ClassFormWorkshopItem);
